import React from 'react';
import {
    Box,
    Dialog,
    DialogContent,
    Stack,
    useMediaQuery,
    useTheme,
    Typography,
    DialogActions,
    TextField,
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import * as Yup from 'yup';
import { Form, FormikProvider, useFormik } from 'formik';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';

import { AlertTypes } from '../../types';
import { AlertService } from '../../services/alert.service';
import { RootState } from '../../store';
import { ComponentPermissionGateway } from '../component-permission-gateway';
import { customerPermissionFlags } from '../../constants/systemPermissions.constants';

interface Props {
    alert: AlertTypes.Model;
    visible: boolean;
    type: string;
    onResolve?: () => void;
    onClose: () => void;
}

export const ResolveAlertModal: React.FC<Props> = ({
    alert,
    type,
    visible,
    onResolve,
    onClose,
}) => {
    const { loading } = useSelector((state: RootState) => state.alert);
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

    const validationSchema = Yup.object().shape({
        resolvedComment: Yup.string().required(
            'Please provide the resolve comment'
        ),
    });

    const formik = useFormik<{ resolvedComment: string }>({
        initialValues: {
            resolvedComment: '',
        },
        validationSchema,
        onSubmit: async (values) => {
            if (alert && alert.id) {
                await AlertService.resolve(
                    alert?.id,
                    values?.resolvedComment,
                    type,
                    (isValid, msg) => {
                        if (isValid) {
                            toast(msg, {
                                autoClose: 1500,
                                type: 'success',
                            });
                        }
                        if (onResolve) {
                            onResolve();
                        }
                        onClose();
                    }
                );
            }
           
        },
    });

    const { handleSubmit, errors, getFieldProps } = formik;

    return (
        <Dialog
            open={visible}
            maxWidth={'md'}
            scroll={'paper'}
            fullWidth={isSmallScreen}
            sx={{ zIndex: 2999 }}
        >
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Stack direction={'row'} justifyContent={'space-between'}>
                    <Typography
                        sx={{ ml: 3, mb: 2, mt: 2 }}
                        fontSize={18}
                        fontWeight={400}
                        color={'text.default'}
                    >
                        Resolve alert
                    </Typography>
                    <Tooltip title="Close">
                        <IconButton
                            onClick={onClose}
                            component="span"
                            sx={{
                                mr: 2,
                                height: 30,
                                width: 30,
                                alignSelf: 'center',
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Box>

            <FormikProvider value={formik}>
                <Form noValidate onSubmit={handleSubmit}>
                    <DialogContent dividers style={{ minWidth: 600 }}>
                        <TextField
                            size={'medium'}
                            fullWidth
                            multiline
                            maxRows={10}
                            minRows={4}
                            label={'Alert resolve comment'}
                            type={'text'}
                            placeholder={
                                'Please, describe the resolution of the alert...'
                            }
                            required={true}
                            error={Boolean(errors?.resolvedComment)}
                            helperText={errors?.resolvedComment}
                            {...getFieldProps('resolvedComment')}
                        />
                    </DialogContent>
                    <DialogActions>
                        <ComponentPermissionGateway
                            role={customerPermissionFlags.TEAM_LEADER}
                        >
                            <LoadingButton
                                variant="contained"
                                type="submit"
                                loading={loading.resolve}
                                fullWidth={isSmallScreen}
                            >
                                Resolve Alert
                            </LoadingButton>
                        </ComponentPermissionGateway>
                    </DialogActions>
                </Form>
            </FormikProvider>
        </Dialog>
    );
};
